import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import {
  getBancoMCPReconciliationStatus,
  getBancoMCPReconciliationCompare,
  deleteBancoMCPJournalTransaction,
  BancoMCPReconciliationAccount,
  BancoMCPReconciliationCompare,
} from '../services/apiService';

type View = 'accounts' | 'compare';

export default function BancoMCPReconciliacaoPage() {
  const [view, setView] = useState<View>('accounts');
  const [accounts, setAccounts] = useState<BancoMCPReconciliationAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<BancoMCPReconciliationAccount | null>(null);
  const [compare, setCompare] = useState<BancoMCPReconciliationCompare | null>(null);
  const [daysBack, setDaysBack] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getBancoMCPReconciliationStatus();
      setAccounts(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const loadCompare = useCallback(async (accountKey: string, days: number) => {
    setLoading(true);
    setError('');
    setDeleteError('');
    try {
      const data = await getBancoMCPReconciliationCompare(accountKey, days);
      setCompare(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Erro ao carregar comparação');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'compare' && selectedAccount) {
      loadCompare(selectedAccount.ledger_account, daysBack);
    }
  }, [view, selectedAccount, daysBack, loadCompare]);

  const handleSelectAccount = (acc: BancoMCPReconciliationAccount) => {
    setSelectedAccount(acc);
    setCompare(null);
    setView('compare');
  };

  const handleBack = () => {
    setView('accounts');
    setSelectedAccount(null);
    setCompare(null);
    setError('');
    setDeleteError('');
  };

  const handleDelete = async (journalId: string) => {
    if (!window.confirm('Remover este lançamento do journal?')) return;
    try {
      await deleteBancoMCPJournalTransaction(journalId);
      setCompare((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          journal_only: prev.journal_only.filter((item) => item.journal.id !== journalId),
        };
      });
      setDeleteError('');
    } catch (e: any) {
      setDeleteError(e?.response?.data?.detail || e?.message || 'Erro ao deletar lançamento');
    }
  };

  if (view === 'accounts') {
    return (
      <div className="p-6 space-y-4 max-w-5xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/banco-mcp" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Reconciliacao Banco MCP</h1>
          </div>
          <button
            onClick={loadAccounts}
            disabled={loading}
            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-1"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>

        <p className="text-sm text-gray-600">
          Clique numa conta para ver a comparacao detalhada banco vs journal.
        </p>

        {error && (
          <div className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading && accounts.length === 0 ? (
          <div className="text-sm text-gray-500">Carregando...</div>
        ) : accounts.length === 0 ? (
          <div className="text-sm text-gray-500">Nenhuma conta mapeada encontrada.</div>
        ) : (
          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Conta</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Banco</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Journal</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Diferenca</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((acc) => (
                  <tr
                    key={acc.ledger_account}
                    onClick={() => handleSelectAccount(acc)}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-2 font-mono text-xs">{acc.ledger_account}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{acc.bank ?? '—'}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{acc.journal ?? '—'}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{acc.diff ?? '—'}</td>
                    <td className="px-4 py-2 text-center">
                      {acc.ok === null ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">—</span>
                      ) : acc.ok ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">OK</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">DIVERGENTE</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // view === 'compare'
  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={handleBack}
            className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </button>
          <h1 className="text-xl font-bold text-gray-900">{selectedAccount?.ledger_account}</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Periodo:</label>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded text-sm"
          >
            <option value={7}>7 dias</option>
            <option value={30}>30 dias</option>
            <option value={60}>60 dias</option>
            <option value={90}>90 dias</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {deleteError && (
        <div className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
          {deleteError}
        </div>
      )}

      {loading && !compare ? (
        <div className="text-sm text-gray-500">Carregando...</div>
      ) : compare ? (
        <div className="space-y-6">
          {/* Casados */}
          <div className="border border-gray-200 rounded">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 text-sm">
                Casados ({compare.matched.length})
              </h3>
            </div>
            {compare.matched.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500">Nenhum.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {compare.matched.map((item, i) => (
                  <li key={i} className="px-4 py-2 text-sm flex items-center gap-3">
                    <span className="text-gray-500 w-24 shrink-0">{item.bank.date}</span>
                    <span className="font-mono text-xs w-28 shrink-0 text-right">{item.bank.amount}</span>
                    <span className="text-gray-700 flex-1 truncate">
                      {item.bank.payee || item.bank.description || '—'}
                    </span>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 shrink-0">
                      {item.match_type}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* So no banco */}
          <div className="border border-gray-200 rounded">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 text-sm">
                So no banco ({compare.bank_only.length})
              </h3>
            </div>
            {compare.bank_only.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500">Nenhum.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {compare.bank_only.map((item, i) => (
                  <li key={i} className="px-4 py-2 text-sm flex items-center gap-3">
                    <span className="text-gray-500 w-24 shrink-0">{item.bank.date}</span>
                    <span className="font-mono text-xs w-28 shrink-0 text-right">{item.bank.amount}</span>
                    <span className="text-gray-700 flex-1 truncate">
                      {item.bank.payee || item.bank.description || '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* So no journal */}
          <div className="border border-amber-200 rounded">
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
              <h3 className="font-semibold text-gray-900 text-sm">
                So no journal — possivel duplicata ({compare.journal_only.length})
              </h3>
            </div>
            {compare.journal_only.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500">Nenhum.</p>
            ) : (
              <ul className="divide-y divide-amber-100">
                {compare.journal_only.map((item, i) => (
                  <li key={i} className="px-4 py-2 text-sm flex items-center gap-3">
                    <span className="text-gray-500 w-24 shrink-0">{item.journal.date}</span>
                    <span className="font-mono text-xs w-28 shrink-0 text-right">{item.journal.amount}</span>
                    <span className="text-gray-700 flex-1 truncate">
                      {item.journal.payee || item.journal.description || '—'}
                    </span>
                    <button
                      onClick={() => handleDelete(item.journal.id)}
                      className="px-2 py-0.5 text-xs bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 shrink-0"
                    >
                      Deletar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
